import isEqual from "lodash/isEqual";

import { TopicDataPathMapping as TopicDataPathBinding } from "@/shared/state/visualization/schema";
import type {
  Position,
  PositionBasePreset,
  PositionTransformation,
} from "@/shared/state/visualization/schema/v1/three-d";

import { getFileAndPath } from "./positionTopicData";

export interface PositionPreset {
  /** The data used to identify and present this preset in a list of presets. */
  name: {
    title: string;
    subtitle: string;
    /** A hint at what file formats commonly use this preset. */
    formatHint: string;
  };

  /**
   * The original type of preset this preset is based on.
   *
   * Should not be changed when duplicating a preset.
   */
  basePreset: PositionBasePreset;

  /**
   * The coordinate transformation necessary to render data from this preset in the 3D panel.
   *
   * Should not be changed when duplicating a preset.
   */
  transformation: PositionTransformation;

  /**
   * The semantic labels to show for each axis. (Lat/Lon/Alt, East/North/Up, etc).
   *
   * Should not be changed when duplicating a preset.
   */
  labels: {
    forward: string;
    right: string;
    up: string;
  };

  scales: {
    forward: number;
    right: number;
    up: number;
  };

  bindings?: {
    forward?: TopicDataPathBinding;
    right?: TopicDataPathBinding;
    up?: TopicDataPathBinding;
  };
}

/**
 * Maps an axis of a position preset to a fileid and topic/message path to the axis' data.
 */
export interface PositionPresetAxisBinding {
  fileId?: string;
  path?: string;
}

export function presetFor(
  position: Position | undefined,
): PositionPreset | undefined {
  if (!position || !position.basePreset) {
    return undefined;
  }
  const basePreset = presets[position.basePreset];
  const scales = {
    forward: position.forwardScale,
    right: position.rightScale,
    up: position.upScale,
  };

  const forwardConfig = getFileAndPath(position.forward);
  const rightConfig = getFileAndPath(position.right);
  const upConfig = getFileAndPath(position.up);

  // If the position is an unmodified version of the preset, then return the preset as-is.
  if (
    isEqual(scales, basePreset.scales) &&
    !forwardConfig &&
    !rightConfig &&
    !upConfig
  ) {
    return basePreset;
  }

  // Otherwise, provide the customized preset.

  return {
    ...basePreset,
    name: {
      ...basePreset.name,
      title: `Custom (${basePreset.name.title})`,
      formatHint: "",
    },
    scales,
    bindings: {
      forward: forwardConfig,
      right: rightConfig,
      up: upConfig,
    },
  };
}

const baseLatLonAltPreset: PositionPreset = {
  name: {
    title: "Lat/Lon/Alt",
    subtitle: "deg/deg/m",
    formatHint: "Common in PX4/ROS",
  },
  labels: {
    right: "Lat",
    forward: "Lon",
    up: "Alt",
  },
  scales: {
    right: 1,
    forward: 1,
    up: 1,
  },
  transformation: "lla_to_enu",
  basePreset: "lat_lon_alt",
};

const baseEastNorthUpPreset: PositionPreset = {
  name: {
    title: "East/North/Up",
    subtitle: "m/m/m",
    formatHint: "Common in ROS",
  },
  labels: {
    right: "East",
    forward: "North",
    up: "Up",
  },
  scales: {
    right: 1,
    forward: 1,
    up: 1,
  },
  transformation: null,
  basePreset: "east_north_up",
};

const baseNorthEastDownPreset: PositionPreset = {
  name: {
    title: "North/East/Down",
    subtitle: "m/m/-m",
    formatHint: "Common in PX4",
  },
  labels: {
    right: "East",
    forward: "North",
    up: "Down",
  },
  scales: {
    right: 1,
    forward: 1,
    up: -1,
  },
  transformation: null,
  basePreset: "north_east_down",
};

const baseForwardRightUpPreset: PositionPreset = {
  name: {
    title: "Forward/Right/Up",
    subtitle: "m/m/m",
    formatHint: "Common in ROS",
  },
  labels: {
    forward: "Forward",
    right: "Right",
    up: "Up",
  },
  scales: {
    forward: 1,
    right: 1,
    up: 1,
  },
  transformation: null,
  basePreset: "forward_right_up",
};

export const presets: Record<PositionBasePreset, PositionPreset> = {
  lat_lon_alt: baseLatLonAltPreset,
  east_north_up: baseEastNorthUpPreset,
  north_east_down: baseNorthEastDownPreset,
  forward_right_up: baseForwardRightUpPreset,
};
